import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CaptchaInput from '@/components/base/CaptchaInput';
import { sendContactEmail } from '@/services/contact.api';

export default function ContactSection() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isZh = i18n.language === 'zh';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    if (!captchaValid) return;
    setSubmitting(true);
    try {
      await sendContactEmail({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-2-line',
      labelZh: '办公地址',
      labelEn: 'Address',
      valueZh: 'Melenhorst 5',
      valueEn: 'Melenhorst 5',
    },
    {
      icon: 'ri-mail-line',
      labelZh: '电子邮箱',
      labelEn: 'Email',
      valueZh: 'info@gtraweb3.com',
      valueEn: 'info@gtraweb3.com',
      href: 'mailto:info@gtraweb3.com',
    },
    {
      icon: 'ri-phone-line',
      labelZh: '联系电话',
      labelEn: 'Phone',
      valueZh: '+86 21 6828 8888',
      valueEn: '+86 21 6828 8888',
      href: 'tel:+862168288888',
    },
    {
      icon: 'ri-time-line',
      labelZh: '工作时间',
      labelEn: 'Working Hours',
      valueZh: '周一至周五 09:00 – 18:00 (GMT+8)',
      valueEn: 'Monday – Friday, 09:00 – 18:00 (GMT+8)',
    },
  ];

  return (
    <section id="contact" className="py-20 md:py-24 bg-[#0A2540]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C7DA0]/20 text-[#7EC8E3] text-xs font-semibold mb-4 tracking-widest uppercase border border-[#2C7DA0]/30">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-message-3-line" />
            </div>
            {t('aboutPage.contact.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('aboutPage.contact.title')}
          </h2>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            {t('aboutPage.contact.subtitle')}
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {/* Contact Info */}
          <div className="space-y-5">
            {contactInfo.map(({ icon, labelZh, labelEn, valueZh, valueEn, href }) => (
              <div key={icon} className="flex items-start gap-4 bg-white/5 rounded-xl p-5 hover:bg-white/8 transition-colors border border-white/10">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#2C7DA0]/20 shrink-0">
                  <i className={`${icon} text-[#2C7DA0] text-lg`} />
                </div>
                <div>
                  <div className="text-white/40 text-xs font-medium mb-1 tracking-widest uppercase">
                    {isZh ? labelZh : labelEn}
                  </div>
                  {href ? (
                    <a
                      href={href}
                      className="text-white text-sm hover:text-[#7EC8E3] transition-colors"
                    >
                      {isZh ? valueZh : valueEn}
                    </a>
                  ) : (
                    <span className="text-white text-sm">{isZh ? valueZh : valueEn}</span>
                  )}
                </div>
              </div>
            ))}

            {/* Social */}
            <div className="bg-white/5 rounded-xl p-5 border border-white/10">
              <div className="text-white/40 text-xs font-medium mb-3 tracking-widest uppercase">
                {isZh ? '关注我们' : 'Follow Us'}
              </div>
              <div className="flex items-center gap-3">
                {[
                  { icon: 'ri-linkedin-box-fill', label: 'LinkedIn' },
                  { icon: 'ri-twitter-x-line', label: 'X' },
                  { icon: 'ri-wechat-fill', label: 'WeChat' },
                  { icon: 'ri-youtube-fill', label: 'YouTube' },
                ].map(({ icon, label }) => (
                  <button
                    key={label}
                    aria-label={label}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-[#2C7DA0]/30 text-white/60 hover:text-white transition-all cursor-pointer"
                  >
                    <i className={`${icon} text-lg`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#2C7DA0]/20 mb-4">
                  <i className="ri-checkbox-circle-fill text-3xl text-[#2C7DA0]" />
                </div>
                <h3 className="text-white font-bold text-xl mb-2">
                  {isZh ? '消息已发送！' : 'Message Sent!'}
                </h3>
                <p className="text-white/60 text-sm">{t('aboutPage.contact.form.success')}</p>
              </div>
            ) : (
              <form
                id="about-contact-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/60 text-xs font-medium mb-1.5">
                      {t('aboutPage.contact.form.name')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('aboutPage.contact.form.ph.name')}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#2C7DA0] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-xs font-medium mb-1.5">
                      {t('aboutPage.contact.form.email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t('aboutPage.contact.form.ph.email')}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#2C7DA0] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/60 text-xs font-medium mb-1.5">
                    {t('aboutPage.contact.form.subject')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder={t('aboutPage.contact.form.ph.subject')}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#2C7DA0] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-white/60 text-xs font-medium mb-1.5">
                    {t('aboutPage.contact.form.message')}
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder={t('aboutPage.contact.form.ph.message')}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#2C7DA0] transition-colors resize-none"
                  />
                  <div className="flex justify-between mt-1">
                    {formData.message.length > 500 && (
                      <span className="text-red-400 text-xs">
                        {isZh ? '留言不能超过500字' : 'Message cannot exceed 500 characters'}
                      </span>
                    )}
                    <span className="ml-auto text-white/30 text-xs">{formData.message.length}/500</span>
                  </div>
                </div>

                {/* CAPTCHA */}
                <div>
                  <label className="block text-white/60 text-xs font-medium mb-1.5">
                    {isZh ? '验证码' : 'CAPTCHA'} <span className="text-red-400">*</span>
                  </label>
                  <CaptchaInput
                    value={captchaValue}
                    onChange={setCaptchaValue}
                    onValidate={setCaptchaValid}
                    isZh={isZh}
                    className="[&_input]:bg-white/10 [&_input]:border-white/20 [&_input]:text-white [&_input]:placeholder-white/30 [&_input]:focus:border-[#2C7DA0] [&_p]:text-white/40"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || formData.message.length > 500 || !captchaValid}
                  className="w-full py-3.5 bg-[#2C7DA0] hover:bg-[#2C7DA0]/90 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? t('common.loading') : t('aboutPage.contact.form.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
